import { CircleAlertIcon } from "lucide-react";
import { Tooltip, TooltipTrigger, TooltipPopup } from "../ui/tooltip";
import type { ModelEsque } from "./providerIconUtils";

const usd = (value: number) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 3,
  }).format(value);

/** Product-facing detail only: never render the provider model id. */
export function ModelDetailsCard({ model }: { model: ModelEsque }) {
  const pricing = model.tokenPricing;
  const rows = pricing
    ? ([
        ["Input", pricing.input],
        ["Cached input", pricing.cachedInput],
        ["Cache writes", pricing.cacheWrite],
        ["Output", pricing.output],
      ] as const)
    : [];
  return (
    <div className="w-72 max-w-full space-y-3 p-4 text-left text-xs leading-relaxed">
      <div>
        <div className="flex items-center justify-between gap-3">
          <p className="text-sm font-semibold text-foreground">{model.name}</p>
          {model.slug === "amoreg-flash" && <Tooltip>
            <TooltipTrigger render={<button type="button" aria-label="High cost details" className="inline-flex shrink-0 items-center gap-1 rounded-sm py-1 text-[10px] font-medium text-amber-700 outline-none focus-visible:ring-2 focus-visible:ring-ring dark:text-amber-400" />}>
              <CircleAlertIcon className="size-3" /> High cost
            </TooltipTrigger>
            <TooltipPopup side="top" className="max-w-64 text-xs leading-relaxed">
              Uses your included allowance quickly. Paid Ultrafast usage is charged at cost, with no 2× markup.
              Set your paid usage limit in Usage or Settings → Account.
            </TooltipPopup>
          </Tooltip>}
        </div>
        <p className="mt-1.5 text-muted-foreground">{model.description}</p>
      </div>
      {pricing && (
        <>
          <div className="border-t border-border pt-3">
            <p className="mb-2 font-medium text-foreground">
              Token prices <span className="font-normal text-muted-foreground">· USD per 1M</span>
            </p>
            <dl className="space-y-1">
              {rows.map(([label, value]) => (
                <div key={label} className="flex justify-between gap-4">
                  <dt className="text-muted-foreground">{label}</dt>
                  <dd className="font-medium tabular-nums">{usd(value)}</dd>
                </div>
              ))}
            </dl>
          </div>
          {pricing.longContext && (
            <p className="text-muted-foreground">
              Above {pricing.longContext.threshold.toLocaleString("en-US")} input tokens:{" "}
              {usd(pricing.longContext.input)} input, {usd(pricing.longContext.cachedInput)} cached,{" "}
              {usd(pricing.longContext.cacheWrite)} cache writes, {usd(pricing.longContext.output)}{" "}
              output per 1M.
            </p>
          )}
          <p className="border-t border-border pt-3 text-muted-foreground">
            Actual token cost counts toward your included allowance in GBP.
            {model.slug === "amoreg-flash" ? " Paid usage is charged at cost." : " Paid usage follows your workspace’s on-demand rate."}
          </p>
        </>
      )}
    </div>
  );
}
